import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Clapperboard } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { useUser } from '../../context/UserContext';
import PixelStage from './PixelStage';
import AvatarCustomizer from './AvatarCustomizer';
import { loadAvatar, makeDefaultAvatar } from './avatar';
import { buildEpisodeRoomScene, episodeDisplayName, truncateName } from './maps';
import {
  createTownEpisodeVisit, createTownProjectVisit, loadTownContinuation, saveTownContinuation,
} from './townContinuation';

/**
 * 像素办公室 · 集功能大厅（三层结构的第 3 层）
 * 路由：/project/:projectId/episode/:episodeId/office
 * 四组功能房（故事制作 / 画布与后期 / 生成工具 / 资产与教学），地垫 Enter 跳真实工作台路由；
 * 出口地垫 / Esc 返回项目楼层（选集）。
 */
export default function PixelEpisodeRoom() {
  const { projectId, episodeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, user } = useUser();
  const displayName = user?.name || userId || '新同事';

  const [avatar, setAvatar] = useState(() => loadAvatar() || makeDefaultAvatar(displayName));
  const [hasSavedAvatar, setHasSavedAvatar] = useState(() => !!loadAvatar());
  const [showCustomizer, setShowCustomizer] = useState(() => !loadAvatar());
  const [projectName, setProjectName] = useState(location.state?.projectName || '');
  const [episodeLabel, setEpisodeLabel] = useState(location.state?.episodeLabel || '');

  // 项目名：优先 location.state，否则拉一次项目列表匹配（刷新/直链场景）
  useEffect(() => {
    if (projectName) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/list`, {
          headers: { 'X-User-ID': userId },
        });
        if (cancelled) return;
        const found = Array.isArray(list) ? list.find((p) => String(p.id) === String(projectId)) : null;
        setProjectName(found?.name || found?.title || '项目空间');
      } catch (e) {
        if (!cancelled) {
          console.error('[pixel-office] 项目名加载失败', e);
          setProjectName('项目空间');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, userId, projectName]);

  useEffect(() => {
    if (!projectId || !projectName) return;
    saveTownContinuation(createTownProjectVisit(
      loadTownContinuation(), { id: projectId, name: projectName }, Date.now(),
    ));
  }, [projectId, projectName]);

  // 集显示名：优先 location.state（集门带入），否则拉分集列表按 id 匹配（刷新/直链场景）
  useEffect(() => {
    if (episodeLabel) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
        if (cancelled) return;
        const found = Array.isArray(list) ? list.find((ep) => String(ep.id) === String(episodeId)) : null;
        setEpisodeLabel(found ? episodeDisplayName(found) : '本集空间');
      } catch (e) {
        if (!cancelled) {
          console.error('[pixel-office] 集信息加载失败', e);
          setEpisodeLabel('本集空间');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, episodeId, episodeLabel]);

  const scene = useMemo(
    () => buildEpisodeRoomScene(projectId, episodeId, projectName || '项目空间', episodeLabel || '本集'),
    [projectId, episodeId, projectName, episodeLabel],
  );

  const welcomeToasts = useMemo(() => ([
    `${episodeLabel || '本集'}的全部功能入口都在这个大厅，Enter 进入`,
    'Esc 或右下角出口返回选集楼层',
  ]), [episodeLabel]);

  const handleAction = useCallback((action) => {
    if (action?.type !== 'route') return;
    const record = createTownEpisodeVisit({
      projectId, projectName, episodeId, episodeLabel, route: action.path,
    }, Date.now());
    if (record) saveTownContinuation(record);
    navigate(action.path, action.state ? { state: action.state } : undefined);
  }, [navigate, projectId, projectName, episodeId, episodeLabel]);

  const handleEscape = useCallback(
    () => navigate(`/project/${projectId}/office`, { state: { projectName: projectName || '项目空间' } }),
    [navigate, projectId, projectName],
  );

  return (
    <div className="pixel-town-page">
      <div className="pixel-town-toolbar">
        <div className="pixel-town-toolbar__identity">
          <span className="pixel-town-toolbar__mark"><Clapperboard size={15} /></span>
          <span className="pixel-town-toolbar__title">
            《{truncateName(projectName || '项目空间', 16)}》
          </span>
          <span className="pixel-town-toolbar__meta">
            {truncateName(episodeLabel || '本集', 16)}
          </span>
        </div>
        <button
          type="button"
          onClick={handleEscape}
          className="pixel-town-button"
          title="返回选集楼层（Esc）"
        >
          <ChevronLeft size={14} /> <span className="pixel-town-button__label">返回选集</span>
        </button>
      </div>

      {/* 舞台 */}
      <div className="pixel-town-stage-shell">
        <PixelStage
          scene={scene}
          avatar={avatar}
          paused={showCustomizer}
          welcomeToasts={welcomeToasts}
          onAction={handleAction}
          onEscape={handleEscape}
          onCustomizeAvatar={() => setShowCustomizer(true)}
        />

        {showCustomizer && (
          <AvatarCustomizer
            initialAvatar={hasSavedAvatar ? avatar : null}
            defaultName={displayName}
            allowCancel={hasSavedAvatar}
            onSave={(next) => { setAvatar(next); setHasSavedAvatar(true); setShowCustomizer(false); }}
            onCancel={() => setShowCustomizer(false)}
          />
        )}
      </div>
    </div>
  );
}
